import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import './right-panel-modal.scss';

export interface RightPanelTab {
    id: string;
    label: React.ReactNode;
    content: React.ReactNode;
}

export interface RightPanelModalApi {
    open: (defaultTabId?: string) => void;
    close: () => void;
    isOpen: boolean;
}

interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    tabs?: RightPanelTab[];
    defaultTabId?: string;
    onTabChange?: (tabId: string) => void;
}

const RightPanelModal = forwardRef<RightPanelModalApi, Props>(({
    isOpen: propIsOpen,
    onClose,
    tabs = [],
    defaultTabId,
    onTabChange
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTabId, setActiveTabId] = useState(defaultTabId || (tabs.length > 0 ? tabs[0].id : ''));

    // Sync with propIsOpen if provided
    useEffect(() => {
        if (propIsOpen !== undefined) {
            setIsOpen(propIsOpen);
        }
    }, [propIsOpen]);

    // Set default tab if provided and activeTabId is not set
    useEffect(() => {
        if (defaultTabId) {
            setActiveTabId(defaultTabId);
        }
    }, [defaultTabId]);

    // Fallback if activeTabId is empty but tabs are available
    useEffect(() => {
        if (!activeTabId && tabs.length > 0) {
            setActiveTabId(tabs[0].id);
        }
    }, [tabs, activeTabId]);

    const handleClose = () => {
        setIsOpen(false);
        onClose?.();
    };

    useImperativeHandle(ref, () => ({
        open: (tabId?: string) => {
            setIsOpen(true);
            if (tabId) {
                setActiveTabId(tabId);
            }
        },
        close: () => {
            handleClose();
        },
        isOpen
    }));

    const activeTab = tabs.find(tab => tab.id === activeTabId);

    return createPortal(
        <div className={classNames('right-panel-modal', {
            'right-panel-modal--open': isOpen
        })}>
            <div className="right-panel-modal__header">
                <div className="right-panel-modal__tabs-nav">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={classNames('right-panel-modal__tab-btn', {
                                'right-panel-modal__tab-btn--active': activeTabId === tab.id
                            })}
                            onClick={() => {
                                setActiveTabId(tab.id);
                                onTabChange?.(tab.id);
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <button className="right-panel-modal__close-btn" onClick={handleClose} aria-label="Cerrar modal">
                    <i className="fa-solid fa-xmark"></i>
                </button>
            </div>
            <div className="right-panel-modal__body">
                {activeTab ? (
                    <div key={activeTab.id} className="right-panel-modal__tab-content">
                        {activeTab.content}
                    </div>
                ) : (
                    <div className="right-panel-modal__empty-content">No hay contenido disponible</div>
                )}
            </div>
        </div>,
        document.body
    );
});

RightPanelModal.displayName = 'RightPanelModal';
export default RightPanelModal;
